import React, { useState, useEffect } from 'react';
import useHandCarryList from './useManageHandCarry';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import AddEditForm from '../../sharedComponents/AddEditForm';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerProfiles, selectCustomerProfiles } from '../../redux/features/customerProfileSlice';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Grid,
  IconButton,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Check as CheckIcon, 
  Edit as EditIcon 
} from '@mui/icons-material';

const HandCarryList = () => {
  const dispatch = useDispatch();
  const profiles = useSelector(selectCustomerProfiles);
  const shopName = useSelector((state) => state.shop.name);

  const [url, setUrl] = useState('');
  const [smileName, setSmileName] = useState('');
  const [profileState, setProfileState] = useState({});
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState({});
  
  const { 
    loading, 
    error, 
    handCarryLists, 
    registerHandCarryList, 
    deleteHandCarryList,
    fetchHandCarryLists,
    updateJpArriveDate,
    updateHandCarryDate,
    checkHandCarryByUrl
  } = useHandCarryList(shopName); // Pass shopName here

  useEffect(() => {
    dispatch(getCustomerProfiles(shopName));
    fetchHandCarryLists();
  }, [dispatch, shopName, fetchHandCarryLists]);

  useEffect(() => {
    const findProfile = () => {
      const profile = profiles.find(profile => profile.smileName === smileName);
      if (profile) {
        setProfileState(profile);
      } else {
        setProfileState(null);
      }
    };
    findProfile();
  }, [smileName, profiles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!smileName) {
      alert('Please select a customer');
      return;
    }
    try {
      await registerHandCarryList({ 
        url,
        customerId: profileState.id 
      });
      setUrl('');
    } catch (err) {
      console.error('Error registering hand carry list:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteHandCarryList(id);
    } catch (err) {
      console.error('Error deleting hand carry list:', err);
    }
  };

  const handleCheck = async (id) => {
    try {
      await updateHandCarryDate(id);
    } catch (err) {
      console.error('Error updating hand carry date:', err);
    }
  };

  const handleEdit = async (item) => {
    setSelectedItem(item);
    setEditData({
      jpArriveDate: item.jpArriveDate ? new Date(item.jpArriveDate) : null
    });
    setShowEditForm(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditDateChange = (date, fieldName) => {
    setEditData(prev => ({
      ...prev,
      [fieldName]: date
    }));
  };

  const handleEditUpdate = async (data) => {
    try {
      await updateJpArriveDate(selectedItem.id, {
        jpArriveDate: data.jpArriveDate ? data.jpArriveDate.toISOString() : null
      });
      setShowEditForm(false);
      setSelectedItem(null);
      setEditData({});
    } catch (err) {
      console.error('Error updating JP arrive date:', err);
    }
  };

  const handleEditClose = () => {
    setShowEditForm(false);
    setSelectedItem(null);
    setEditData({});
  };

  // Form configuration for AddEditForm
  const editFormItems = [
    {
      name: 'jpArriveDate',
      title: 'JP Arrive Date',
      type: 'date'
    }
  ];

  return (
    <>
      <Grid container spacing={3}>
        {/* Left Side - Table Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              รายการหิ้ว
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="8%">No.</TableCell>
                    <TableCell width="12%">Date</TableCell>
                    <TableCell width="15%">Customer</TableCell>
                    <TableCell width="20%">URL</TableCell>
                    <TableCell width="15%">ชนิดสินค้า</TableCell>
                    <TableCell width="15%">Tracking Jp</TableCell>
                    <TableCell width="15%">ArriveDate</TableCell>
                    <TableCell width="10%">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {handCarryLists && handCarryLists.length > 0 && handCarryLists.map((item, index) => (
                    <TableRow key={item._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {item.contact}
                      </TableCell>
                      <TableCell>
                        <Button
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: 'none' }}
                        >
                          {item.url}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {item.airItemTypeName || '-'}
                      </TableCell>
                      <TableCell>
                        {item.trackingJp || '-'}
                      </TableCell>
                      <TableCell>
                        {item.jpArriveDate ? new Date(item.jpArriveDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            onClick={() => handleCheck(item.id)}
                            color="success"
                            size="small"
                            title="Check"
                            disabled={loading}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleEdit(item)}
                            color="primary"
                            size="small"
                            title="Edit"
                            disabled={loading}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDelete(item.id)}
                            color="error"
                            size="small"
                            title="Delete"
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Right Side - Input Section */}
        {/* <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Add New Hand Carry URL
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ mb: 2 }}>
                <ProfileCombo 
                  profiles={profiles}
                  onChange={(smileName) => setSmileName(smileName)}
                />
              </Box>
              <TextField
                fullWidth
                label="URL"
                variant="outlined"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                type="url"
                placeholder="Enter URL"
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !url || !smileName}
                startIcon={<AddIcon />}
                fullWidth
              >
                {loading ? 'Adding...' : 'Add URL'}
              </Button>
            </Box>
          </Paper>
        </Grid> */}
      </Grid>

      {/* Edit Form Modal */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <AddEditForm
              name="Edit JP Arrive Date"
              items={editFormItems}
              data={editData}
              mode="edit"
              closeForm={handleEditClose}
              handleUpdate={handleEditUpdate}
              handleInputChange={handleEditInputChange}
              handleDateChange={handleEditDateChange}
              stateValue={editData}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HandCarryList;